import { computed, defineAsyncComponent, defineComponent, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useCentralStore } from 'src/stores/central';
import { useConvoStore } from 'src/stores/conversations';
import { useViewStore } from 'src/stores/view';
import { zip } from 'rxjs';
import { debounceTime, filter, map, switchMap, tap } from 'rxjs/operators';
import { rxInitSubjectCallback, useObservable } from 'src/plugins/rxjs/base';
import date from 'quasar/src/utils/date/date.js';
const { getDateDiff } = date;
import { ChatHostInfoPlugin } from 'src/features/messaging/chat/components/methods/queryHostInfo';
import { ChatMiscDataPlugin } from 'src/features/messaging/chat/components/methods/misc';
import { ChatCheckSendingPlugin } from 'src/features/messaging/chat/components/methods/checkSending';
import { ReceiverOnlinePlugin } from 'src/features/messaging/chat/components/methods/checkReceiverOnline';
import { sharePlugin } from 'src/plugins/general/share';
import report from './report.vue';
import generalDialog from 'src/components/general/dialog.vue';
export default defineComponent({
    name: 'chat-header',
    components: {
        report,
        generalDialog,
        standardAvatar: defineAsyncComponent(() => import(
        /* webpackChunkName: "Standard Avatar" */
        'components/image/standardAvatar.vue'))
    },
    props: {
        backLabel: {
            type: String,
            default: 'Inbox'
        },
        listingIsActive: {
            type: Object,
            default: () => {
                return {
                    active: true,
                    approved: true
                };
            }
        },
        listingLoading: Boolean,
        listing: {
            type: Object,
            required: true
        },
        routeString: {
            type: String,
            required: true
        },
    },
    emits: [
        'close-chat',
        'create-custom-order'
    ],
    setup(props, context) {
        const vueRoute = useRoute();
        const convoStore = useConvoStore();
        const centralStore = useCentralStore();
        const platformInfo = computed(() => centralStore.platformInfo);
        /**
         * Data
         */
        const { convo, appType, isChatUser, receiver, } = ChatMiscDataPlugin();
        /**
         * Share
         */
        const { menu, showShare, shareRoute, share } = sharePlugin();
        /**
        * Receiver online
        */
        const { receiverOnline } = ReceiverOnlinePlugin(receiver);
        /**
         * Host info
         */
        const { hostContact } = ChatHostInfoPlugin();
        const hostContactShow = ref(false);
        /**
         * Listing Path
         * */
        const listingRedirect = computed(() => {
            return appType.value === 'getspaces' ? 'Listing' : 'Property';
        });
        /**
         * Receiver typing
         */
        const isTyping = ref(false);
        const receiverInChat = ref(false);
        const chatLoading = computed(() => convoStore.chatLoading);
        const { subject: infoQuery$ } = rxInitSubjectCallback();
        useObservable(infoQuery$.pipe(filter(() => !chatLoading.value), filter(() => !process.env.SERVER), debounceTime(500), switchMap(() => {
            return import(/* webpackChunkName: "firebaseMethod" */ 'src/_helpers/firebaseMethods');
        }), map(({ default: firebaseMethods }) => firebaseMethods), switchMap(firebaseMethods => {
            if (process.env.SERVER) {
                console.log("hit 3");
            }
            return zip(firebaseMethods(), import(
            /* webpackChunkName: "firebaseMethod" */
            'firebase/firestore'), import(
            /* webpackChunkName: "firebaseRx" */
            'rxfire/firestore'));
        }), switchMap(([{ db }, { doc: fbDoc }, { doc }]) => {
            /**
             * Check if the receiver is online
             */
            return doc(fbDoc(db, 'chat', convo.value.id)).pipe(map(v => v.data()), filter((chatRoomData) => chatRoomData !== null), map((chatRoomData) => chatRoomData), tap((chatRoomData) => {
                /**
                 * Check if the opposing user is typing
                 */
                isTyping.value = chatRoomData[`typing_${receiver.value}`] || false;
            }), tap((chatRoomData) => {
                /**
                 * Check if opposing user is active recently
                 */
                const receiverLastActive = getDateDiff(new Date(), new Date(chatRoomData[`last_active_${receiver.value}`]), 'seconds');
                receiverInChat.value = (chatRoomData[`is_active_${receiver.value}`] === true) && (receiverLastActive <= 60 * 60);
            }));
        })));
        /**
         * Reroutes
         */
        const { savedPath, checkSend } = ChatCheckSendingPlugin(() => { context.emit('close-chat'); }, () => { context.emit('close-chat'); });
        /**
         * Check before redirecting to host dashboard
         */
        function checkDashboardRoute() {
            if ((vueRoute.name === 'Host Upcoming Bookings') &&
                (vueRoute.query.user_pk === receiver.value.toString())) {
                checkSend();
            }
            savedPath.value = {
                name: 'Host Upcoming Bookings',
                query: {
                    user_pk: receiver.value
                }
            };
            context.emit('close-chat');
        }
        const viewStore = useViewStore();
        /**
         * Show calendar popup
         */
        function viewCalendar() {
            viewStore.toggleShowCalendar(true);
        }
        /**
         * Check if sending before redirecting to
         * listing page
         */
        function checkRoute() {
            if (vueRoute.name === listingRedirect.value) {
                if (vueRoute.params.id === props.routeString) {
                    checkSend();
                    menu.value = false;
                }
            }
            savedPath.value = {
                name: listingRedirect.value,
                params: {
                    id: props.routeString
                }
            };
        }
        /**
         * Check before redirecting to host profile page
         */
        function checkProfileRoute() {
            if (['Host Spaces For Rent', 'Host Profile'].indexOf(vueRoute.name?.toString() || '') > -1) {
                if (vueRoute.params.id === isChatUser.value) {
                    checkSend();
                }
            }
            savedPath.value = {
                name: 'Host Spaces For Rent',
                params: {
                    id: isChatUser.value
                }
            };
        }
        const chatMounted = ref(false);
        onMounted(() => {
            chatMounted.value = true;
            if (chatLoading.value)
                return;
            infoQuery$.next('');
        });
        watch(chatLoading, (newValue) => {
            if (newValue || !chatMounted.value)
                return;
            infoQuery$.next('');
        });
        watch(convo, (newValue, oldValue) => {
            // check for changes in convo
            if (chatMounted.value && (!oldValue || ((newValue && oldValue) &&
                (newValue.id !== oldValue.id)))) {
                infoQuery$.next('');
            }
        });
        return {
            platformInfo,
            appType,
            convo,
            isChatUser,
            receiver,
            hostContact,
            hostContactShow,
            menu,
            showShare,
            shareRoute,
            share,
            listingRedirect,
            receiverOnline,
            isTyping,
            checkRoute,
            checkDashboardRoute,
            viewCalendar,
            checkProfileRoute,
            checkSend,
        };
    }
});
